import { useNavigate } from '@tanstack/react-router';
import {
  completeProfile,
  isNeedsProfile,
  type AuthResponse,
  type AuthResult,
  type NeedsProfile,
} from '@/lib/auth';
import { AnalyticsEvent, pageSnapshotProperties, trackEvent } from '@/lib/analytics';
import { setPendingAuthMethod, type AuthMethod } from '@/lib/last-auth-method';
import { splitFullName } from '@/lib/string';

type NavigateFn = ReturnType<typeof useNavigate>;

/**
 * Post-verify guard shared by every auth surface that exchanges a magic
 * code / OAuth token for a session. When the backend says the user still needs
 * to finish their profile, route to /auth/complete-profile carrying the pending
 * token (and prefilled name if present). Returns `true` when it navigated (a
 * type predicate, so the caller's `else`/after-return path narrows `result` to
 * `AuthResponse`), letting callers early-return before their own post-verify branch.
 */
export function navigateToCompleteProfile(
  navigate: NavigateFn,
  result: AuthResult,
  options?: { replace?: boolean; method?: AuthMethod },
): result is NeedsProfile {
  if (!isNeedsProfile(result)) return false;
  // The sign-up finishes on /complete-profile, which commits the "last used"
  // hint from this pending value. OAuth already set it on the button click.
  if (options?.method) setPendingAuthMethod(options.method);
  void navigate({
    to: '/auth/complete-profile',
    ...(options?.replace ? { replace: true } : {}),
    search: {
      token: result.pendingToken,
      ...(result.prefillName ? { name: result.prefillName } : {}),
    },
  });
  return true;
}

/**
 * Google / Microsoft OAuth and One Tap already hand us the user's display
 * name, so finish the profile with it instead of stopping on
 * /auth/complete-profile for a click that changes nothing. Returns the session
 * on success, or `null` — a non-OAuth token, no provider name, or a failed
 * submit — so the caller falls back to the complete-profile screen.
 */
export async function autoCompleteOAuthProfile(
  result: NeedsProfile,
): Promise<AuthResponse | null> {
  const name = result.prefillName?.trim();
  if (!name || !isOAuthPendingToken(result.pendingToken)) return null;
  try {
    const auth = await completeProfile(result.pendingToken, name);
    // Same event CompleteProfilePage sends on submit. No `Signed In` here —
    // for an OAuth pending token the api already sent it at the callback.
    const { firstName, lastName } = splitFullName(name);
    trackEvent(AnalyticsEvent.NameAdded, {
      first_name: firstName,
      last_name: lastName,
      name,
      ...pageSnapshotProperties(),
    });
    return auth;
  } catch {
    return null;
  }
}

/** The sign-up methods whose `Signed In` the api sends at the OAuth callback. */
const API_TRACKED_SIGNIN_METHODS: ReadonlySet<string> = new Set(['google', 'microsoft']);

/**
 * Whether the pending-profile token was minted by Google / Microsoft OAuth or
 * One Tap. Reads the token's own `authMethod` claim without verifying it — it
 * only decides an analytics call, and the api verifies the token on submit.
 * An unreadable token, or one with no claim, counts as not-OAuth (the api's
 * own fallback for such a token is magic link).
 */
export function isOAuthPendingToken(pendingToken: string): boolean {
  try {
    const payload = pendingToken.split('.')[1];
    if (!payload) return false;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { authMethod } = JSON.parse(json) as { authMethod?: unknown };
    return typeof authMethod === 'string' && API_TRACKED_SIGNIN_METHODS.has(authMethod);
  } catch {
    return false;
  }
}
